import React, { useEffect, useState, useMemo } from 'react';

import { bffAuthService } from '../../services/BffAuthService';

import { styled, useTheme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';

import { IconButton, Menu, Tooltip, MenuItem, Typography, Toolbar, Box, Button, Avatar, Divider } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';

import { useTranslation } from 'react-i18next';
import { useUserManager } from '../../hooks/managers/useUserManager';
import { hostServerAddress, userServerAddress, mainHeaderHeight, toolBarPaddingLeftRight } from '../../common/MetaSetting';
import { ComponentStyles } from '../../common/ThemeColors';

import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../constants';
import { useMessageStore } from '../../stores/messageStore';
import { UserHeader } from '../components/UserHeader';
import { ShareMemoDialog } from '../components/ShareMemoDialog';
import { useMemoStore } from '../../stores/memoStore';

const headerHeight = mainHeaderHeight;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  })
}));

export const Header = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  const { currentUser } = useUserManager();
  const isAuthenticated = bffAuthService.isAuthenticated;
  const totalUnreadCount = useMessageStore((state) => state.totalUnreadCount);
  const conversations = useMessageStore((state) => state.conversations);
  const setCurrentConversation = useMessageStore((state) => state.setCurrentConversation);

  const currentMemo = useMemoStore((state) => state.currentMemo);
  const currentViewMemo = useMemoStore((state) => state.currentViewMemo);

  // 判断当前是否有 memo 可分享
  const shareMemoInfo = useMemo(() => {
    // 优先使用 currentViewMemo（memo 查看页面）
    if (currentViewMemo?.guid) {
      return {
        title: currentViewMemo.title || 'Untitled',
        url: `${window.location.origin}/view/memo/${currentViewMemo.guid}`,
      };
    }
    // 其次使用 currentMemo（编辑页面）
    if (currentMemo?.guid) {
      return {
        title: currentMemo.title || 'Untitled',
        url: `${window.location.origin}/view/memo/${currentMemo.guid}`,
      };
    }
    // 从 URL 路径中提取 memoId
    const memoMatch = location.pathname.match(/\/view\/memo\/([^/]+)/);
    if (memoMatch) {
      return {
        title: currentViewMemo?.title || 'Memo',
        url: `${window.location.origin}/view/memo/${memoMatch[1]}`,
      };
    }
    return null;
  }, [currentViewMemo, currentMemo, location.pathname]);

  const handleShareClick = () => {
    setShareDialogOpen(true);
  };

  // 处理来自 memo-flutter 的分享触发（URL 参数 action=share）
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'share' && shareMemoInfo) {
      setShareDialogOpen(true);
      // 清除 URL 中的 action 参数，避免刷新时重复触发
      params.delete('action');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [isAuthenticated, shareMemoInfo]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleUnreadClick = () => {
    const isMessagesPage = location.pathname === ROUTES.messages;
    
    if (!isMessagesPage) {
      navigate(`${ROUTES.messages}?selectFirst=true`);
    } else {
      const firstConversation = Array.from(conversations.values())[0];
      if (firstConversation) {
        setCurrentConversation(firstConversation.id);
      }
    }
  };

  let mainTitle = t('appTitle');

  return (
    <AppBar
      position="fixed"
      sx={{
        flexGrow: 0,
        // 对齐 memo-flutter AppBar:背景 appBarBackground(浅白/深 #1A1A1A)
        backgroundColor: 'background.paper',
        color: 'primary.main'
      }}
    >
      <Toolbar variant="dense" sx={{ margin: 0, paddingLeft: toolBarPaddingLeftRight as any, paddingRight: toolBarPaddingLeftRight as any }}>
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'text.primary',
            height: `${headerHeight}`,
            padding: '5px',
            borderRadius: ComponentStyles.base.borderRadius.default,
            bgcolor: 'background.paper'
          }}
        >
          <Avatar src="/logo192.png" />
          <Box>
            <span style={{ color: theme.palette.primary.main }}>{t('wordPrefixWise')}</span>
            <span style={{ color: theme.palette.text.secondary }}>{t('wordLeftWise')}</span>
            <span style={{ color: theme.palette.primary.main }}>{t('wordPrefixMemo')}</span>
            <span style={{ color: theme.palette.text.secondary }}>{t('wordLeftMemo')}</span>
          </Box>
        </Box>
        {isAuthenticated && shareMemoInfo && !location.pathname.startsWith(ROUTES.edit) && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 0.5 }} />
            <Tooltip title={t('shareMemoTitle')}>
              <IconButton
                onClick={handleShareClick}
                size="small"
                sx={{ color: 'text.primary' }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'primary.main'
          }}
        >
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
            alignItems: 'center',
            flexGrow: 0
          }}
        >
          {isAuthenticated ? (
            <>
              <UserHeader
                nickName={currentUser?.nickName}
                name={currentUser?.name}
                username={currentUser?.username}
                avatarPath={currentUser?.avatarPath}
                unreadCount={totalUnreadCount}
                onAvatarClick={handleOpenUserMenu}
                onUnreadClick={handleUnreadClick}
              />
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem dense key="profileItem" onClick={() => window.open('https://user.wisememo.com', '_blank')}>
                  <Typography textAlign="center" component="span">
                    {t('buttonProfile')}
                  </Typography>
                </MenuItem>
                <MenuItem dense key="logoutItem" onClick={() => bffAuthService.logout().then(() => window.location.href = '/login?logged_out=true')}>
                  <Typography textAlign="center" component="span">
                    {t('buttonLogout')}
                  </Typography>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Box>
              <Button variant="outlined" size="small" sx={{ margin: 0.5 }}>
                {t('buttonRegister')}
              </Button>
              <Button variant="contained" size="small" sx={{ margin: 0.5 }} onClick={() => {
                bffAuthService.login();
              }}>
                {t('buttonLogin')}
              </Button>
            </Box>
          )}
        </Box>
      </Toolbar>
      {shareMemoInfo && !location.pathname.startsWith(ROUTES.edit) && (
        <ShareMemoDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          memoTitle={shareMemoInfo.title}
          memoUrl={shareMemoInfo.url}
          onShareSuccess={() => navigate(`${ROUTES.messages}?selectFirst=true`)}
        />
      )}
    </AppBar>
  );
};
